import {
  Button,
  Modal,
  Box,
  TextField,
  useTheme,
  MenuItem,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { tokens } from "../../theme";
import { checkInBooking, checkOutBooking } from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  guest_mobile: Yup.string().required("Phone is required"),
  // .matches(/^[0-9]{10}$/, "Phone must be 10 digits"),
  email: Yup.string().email("Invalid email").required("Email is required"),
});

const BookingActionButton = ({ bookingDetails }) => {
  const [open, setOpen] = useState(false);
  const [mappedRooms, setMappedRooms] = useState([]);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const { hotelProfile, isLoading } = useSelector((state) => {
    return {
      hotelProfile: state.registration.hotelProfile,
      isLoading: state.loading,
    };
  });
  const isBillingEnabled = hotelProfile?.billing_enabled;

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: colors.primary[400],
    outline: "none",
    boxShadow: 24,
    p: 2,
    maxHeight: "90vh", // Added
    overflowY: "auto", // Added
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: bookingDetails?.title || "Mr",
      name: bookingDetails?.name || "",
      email: bookingDetails?.email || "",
      guest_mobile: bookingDetails?.guest_mobile || "",
      guest_address: bookingDetails?.guest_address || "",
      preference: bookingDetails?.preference || "",
      gst_no: bookingDetails?.gst_no || "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          booking_id: bookingDetails.booking_id,
          process_status: "checked_in",
          title: values.title,
          name: values.name,
          email: values.email,
          guest_mobile: values.guest_mobile,
          guest_address: values.guest_address,
          preference: values.preference,
          gst_no: values.gst_no,
          room_mappings: mappedRooms,
        };

        const success = await dispatch(checkInBooking(payload));
        if (success) {
          toast.success("Check-in successful");
        }
        setOpen(false);
      } catch (err) {
        console.error("Check-in failed", err);
      }
    },
  });

  const handleCheckOut = useCallback(async () => {
    try {
      const isSameDate = moment
        .unix(bookingDetails.checkout_date)
        .isSame(moment(), "day");
      if (isSameDate) {
        const success = await dispatch(
          checkOutBooking({
            booking_id: bookingDetails.booking_id,
            process_status: "checked_out",
          })
        );
        if (success) {
          toast.success("Check-out successful");
        }
      } else {
        toast.error(
          "Check-out allowed only on the scheduled date. Update checkout date for early check-out."
        );
      }
    } catch (err) {
      console.error("Check-out failed", err);
    }
  }, [bookingDetails?.booking_id]);

  const handleClick = useCallback(() => {
    if (bookingDetails.process_status === "pending") {
      setOpen(true);
    } else if (bookingDetails.process_status === "checked_in") {
      handleCheckOut();
    }
  }, [bookingDetails.process_status, handleCheckOut]);

  const label = useMemo(() => {
    if (bookingDetails.process_status === "pending") return "Check In";
    if (bookingDetails.process_status === "checked_in") return "Check Out";
    return "";
  }, [bookingDetails.process_status]);

  useEffect(() => {
    if (open && bookingDetails?.rooms?.length > 0) {
      const initialMapped = bookingDetails.rooms.map((room) => ({
        internalRoomNo: room?.room_id?.room_no || "",
        roomType: room?.room_id?.roomtype_id?.name || "",
        roomId: room?._id,
        actualRoomNo: "", // input
      }));
      setMappedRooms(initialMapped);
    }
  }, [open, bookingDetails?.rooms]);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        disabled={bookingDetails?.status === "cancelled"}
        onClick={handleClick}
      >
        <b>{label}</b>
      </Button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={modalStyle} component="form" onSubmit={formik.handleSubmit}>
          <h3>Guest Check-In</h3>

          <TextField
            fullWidth
            select
            label="Title"
            name="title"
            margin="dense"
            value={formik.values.title}
            onChange={formik.handleChange}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
          >
            <MenuItem value="Mr">Mr</MenuItem>
            <MenuItem value="Mrs">Mrs</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label="Name"
            name="name"
            margin="dense"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <TextField
            fullWidth
            label="Email"
            name="email"
            margin="dense"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <TextField
            fullWidth
            label="Mobile"
            name="guest_mobile"
            type="tel"
            margin="dense"
            value={formik.values.guest_mobile}
            // onChange={formik.handleChange}
            onChange={(e) => {
              const digitsOnly = e.target.value
                .replace(/[^0-9]/g, "")
                .slice(0, 10); // 10 digits max
              formik.setFieldValue("guest_mobile", digitsOnly);
            }}
            error={
              formik.touched.guest_mobile && Boolean(formik.errors.guest_mobile)
            }
            helperText={
              formik.touched.guest_mobile && formik.errors.guest_mobile
            }
          />

          <TextField
            fullWidth
            label="Address"
            name="guest_address" // ✅ Fix
            margin="dense"
            multiline
            rows={2}
            value={formik.values.guest_address}
            onChange={formik.handleChange}
            error={
              formik.touched.guest_address &&
              Boolean(formik.errors.guest_address)
            }
            helperText={
              formik.touched.guest_address && formik.errors.guest_address
            }
          />

          <TextField
            fullWidth
            label="Preference"
            name="preference"
            margin="dense"
            value={formik.values.preference}
            onChange={formik.handleChange}
            error={
              formik.touched.preference && Boolean(formik.errors.preference)
            }
            helperText={formik.touched.preference && formik.errors.preference}
          />
          <TextField
            fullWidth
            name="gst_no"
            label="GST No"
            margin="dense"
            value={formik.values.gst_no}
            onChange={formik.handleChange}
            error={formik.touched.gst_no && Boolean(formik.errors.gst_no)}
            helperText={formik.touched.gst_no && formik.errors.gst_no}
          />
          {isBillingEnabled && mappedRooms.length > 0 && (
            <Box mt={2}>
              <h4>Room Mapping (Internal → Actual)</h4>
              {mappedRooms.map((room, index) => (
                <Box
                  key={room.roomId}
                  display="flex"
                  alignItems="center"
                  mb={1}
                  gap={1}
                >
                  <TextField
                    label="Internal Room"
                    value={`${room.internalRoomNo} (${room.roomType})`}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={{ width: "50%" }}
                  />
                  <TextField
                    label="Actual Room No"
                    variant="outlined"
                    size="small"
                    value={room.actualRoomNo}
                    onChange={(e) => {
                      const updated = [...mappedRooms];
                      updated[index].actualRoomNo = e.target.value;
                      setMappedRooms(updated);
                    }}
                    sx={{ width: "50%" }}
                  />
                </Box>
              ))}
            </Box>
          )}
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button onClick={() => setOpen(false)} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={formik.isSubmitting}
            >
              Submit Check-In
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default BookingActionButton;
